import React, { useEffect, useState } from "react";
import { AlertTriangle, FileText, Loader2, ShieldCheck } from "lucide-react";
import { http } from "../lib/api";

export default function EvidenceCitationViewer() {
  const [finding, setFinding] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let mounted = true;
    http
      .get("/findings/current")
      .then(({ data }) => {
        if (mounted) setFinding(data);
      })
      .catch((err) => {
        if (mounted) {
          setError(err?.response?.data?.detail || "当前研判结论暂时不可用");
        }
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, []);

  if (loading) {
    return (
      <div className="flex items-center gap-2 rounded-xl border border-edge well p-5 text-sm text-slate-400">
        <Loader2 size={16} className="animate-spin text-aegis-cyan" /> 正在调阅结构化研判结论…
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center gap-2 rounded-xl border border-aegis-red/30 bg-aegis-red/5 p-5 text-sm text-aegis-red" role="alert">
        <AlertTriangle size={16} /> {error}
      </div>
    );
  }

  if (!finding) return null;

  return (
    <section className="rounded-xl border border-aegis-red/30 bg-panel/70 p-5" data-testid="evidence-citation-viewer">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-aegis-cyan">司法级研判结论</p>
          <h2 className="mt-1 font-heading text-xl font-light text-slate-100">{finding.finding_id}</h2>
        </div>
        <span className="rounded-md border border-aegis-red/40 bg-aegis-red/10 px-2.5 py-1 font-mono text-[11px] text-aegis-red">
          {finding.risk_level}
        </span>
      </div>

      <p className="mt-4 text-sm leading-relaxed text-slate-200">{finding.verdict}</p>

      <div className="mt-5 space-y-3">
        {finding.citations.map((citation) => (
          <article key={citation.citation_id} className="rounded-lg border border-edge well p-4" data-testid={`citation-${citation.citation_id}`}>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex min-w-0 items-center gap-2">
                <FileText size={15} className="shrink-0 text-aegis-purple" />
                <span className="truncate font-mono text-xs text-slate-100">{citation.file_name}</span>
              </div>
              <span className="font-mono text-[10px] text-aegis-emerald">{citation.citation_id} · WORM #{citation.worm_block_height}</span>
            </div>

            <blockquote className="mt-3 border-l-2 border-aegis-cyan/50 pl-3 text-sm leading-relaxed text-slate-300">
              “{citation.exact_quote}”
            </blockquote>

            <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 font-mono text-[10px] text-slate-400">
              <span>SHA-256: {citation.file_sha256}</span>
              {citation.page_number != null && <span>页码: {citation.page_number}</span>}
              {citation.line_number != null && <span>行号: {citation.line_number}</span>}
            </div>

            {citation.bounding_boxes?.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-2">
                {citation.bounding_boxes.map((box, index) => (
                  <span key={`${citation.citation_id}-box-${index}`} className="inline-flex items-center gap-1 rounded border border-aegis-emerald/30 bg-aegis-emerald/5 px-2 py-1 font-mono text-[10px] text-aegis-emerald">
                    <ShieldCheck size={11} /> 高亮坐标 {box.x}, {box.y} · {box.width}×{box.height}
                  </span>
                ))}
              </div>
            )}
          </article>
        ))}
      </div>
    </section>
  );
}
